// Users › Fleet admins (wave 3, W2_Portal_Tables look): the modern table
// with one person cell (photo, name, phone), Add fleet admin top right, Edit
// in the row, Delete after a question that names the person.
//
// Data, exactly as before: the user list already loaded (fetchUsersOnce on
// arrival) filtered to fleet admins. The approval switch writes ONLY
// `approved` (updateUserFields) and needs critical edits on; Delete calls
// deleteUser(id) then fetchUsersOnce(), only with critical edits on (demo
// mode says so instead). The CSV has the same columns as before (no print)
// and needs critical edits on; phone and email are hidden in demo mode.
// Edit / Add open the user form with the page number to come back to.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it never opens the row and
// is not there while numbers are hidden.
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import { tokens as c } from '../theme';
import { showToast, confirmDialog } from '../components/feedback';
import { CopyButton, DataTable, PageHeader, StatusChip, calmCaps, formatPhoneDisplay, phoneMatches, usePageState } from '../components/modern';

const nameOf = (u) => `${(u && u.firstName) || ''} ${(u && u.lastName) || ''}`.trim();
const initialsOf = (u) =>
  [u && u.firstName, u && u.lastName]
    .map((n) => String(n || '').trim().charAt(0))
    .join('')
    .toUpperCase();

export default function FleetAdmins() {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const navigate = useNavigate();
  const { deleteUser, fetchUsersOnce, updateUserFields } = api;
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const usersLoading = useSelector((state) => state.usersdata.loading);
  const usersError = useSelector((state) => state.usersdata.error);
  const dispatch = useDispatch();
  const { state } = useLocation();
  // The page and the table's search are kept while staff open a record and
  // come back (owner, 28 Oct); components/modern/pageState.js.
  const [page, setPage] = usePageState('fleet_admins', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);

  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  const data = staticusers ? staticusers.filter((user) => user.usertype === 'fleetadmin') : [];
  const listFailed = !staticusers && !usersLoading && !!(usersError && usersError.flag);
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const hiddenText = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const yesText = L('yes', 'Yes');
  const noText = L('no', 'No');

  // Writes ONLY the switch's field (see Drivers.js: a full-row editUser()
  // from the cached row wipes anything written server-side since).
  const handelApproved = (rowData) => {
    const patch = { approved: !rowData.approved };
    dispatch(updateUserFields(rowData.id, patch));
    return { ...rowData, ...patch };
  };

  const navState = { state: { pageNo: page } };
  const addFleetAdmin = () => navigate('/users/edituser/fleetadmin', navState);
  const goEdit = (rowData) => navigate(`/users/edituser/fleetadmin/${rowData.id}`, navState);

  const handleDelete = async (rowData) => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const name = nameOf(rowData) || (allowPrivate ? formatPhoneDisplay(rowData.mobile) : '');
    const ok = await confirmDialog({
      title: L('fleetadmins_delete_title', 'Delete fleet admin {{name}}?', { name }),
      body: L('fleetadmins_delete_body', "This removes the fleet admin's account. It cannot be undone."),
      confirmLabel: L('delete', 'Delete'),
      cancelLabel: L('confirm_cancel', 'Cancel'),
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteUser(rowData.id));
    dispatch(fetchUsersOnce());
  };

  const subText = { fontSize: 13, lineHeight: 1.45, color: c.INK_2 };
  const columns = [
    {
      id: 'person',
      title: L('name', 'Name'),
      field: 'firstName',
      sortValue: (row) => nameOf(row),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
          {row.profile_image ? (
            <Avatar src={row.profile_image} alt="" sx={{ width: 36, height: 36 }} />
          ) : (
            <Avatar aria-hidden="true" sx={{ width: 36, height: 36, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 14, fontWeight: 600 }}>
              {initialsOf(row) || '?'}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}>{nameOf(row) || L('no_name', 'No name')}</Box>
            <Typography component="div" sx={{ ...subText, display: 'flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {allowPrivate ? formatPhoneDisplay(row.mobile) : hiddenText}
              {allowPrivate ? <CopyButton value={row.mobile} phone what={phoneWord} sx={{ my: -0.5 }} /> : null}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'email',
      title: t('email'),
      field: 'email',
      sortValue: (row) => (allowPrivate ? row.email || '' : ''),
      render: (row) => (
        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
          {allowPrivate ? row.email || '' : hiddenText}
        </Box>
      ),
    },
    { id: 'createdAt', title: t('createdAt'), field: 'createdAt', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'approved',
      title: calmCaps(t('account_approve')),
      field: 'approved',
      sortValue: (row) => (row.approved ? 1 : 0),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StatusChip
            tone={row.approved ? 'success' : 'neutral'}
            icon={row.approved ? <CheckRoundedIcon /> : <BlockRoundedIcon />}
            label={row.approved ? L('approved', 'Approved') : calmCaps(L('not_approved', 'Not approved'))}
          />
          <Switch
            size="small"
            disabled={!settings.AllowCriticalEditsAdmin}
            checked={!!row.approved}
            onChange={() => handelApproved(row)}
            inputProps={{ 'aria-label': `${calmCaps(t('account_approve'))}: ${nameOf(row)}` }}
          />
        </Box>
      ),
    },
  ];

  // The same CSV columns as before (the photo's address included).
  const exportColumns = [
    { title: t('createdAt'), field: 'createdAt', type: 'datetime' },
    { title: t('first_name'), field: 'firstName' },
    { title: t('last_name'), field: 'lastName' },
    { title: t('mobile'), value: (row) => (allowPrivate ? row.mobile || '' : hiddenText) },
    { title: t('email'), value: (row) => (allowPrivate ? row.email || '' : hiddenText) },
    { title: t('profile_image'), field: 'profile_image' },
    { title: t('account_approve'), value: (row) => (row.approved ? yesText : noText) },
  ];

  // Name always; phone and email only when they may be shown.
  const searchFn = (row, q, ql) => {
    if (nameOf(row).toLowerCase().includes(ql)) return true;
    if (!allowPrivate) return false;
    if (phoneMatches(row.mobile, q)) return true;
    return String(row.email || '').toLowerCase().includes(ql);
  };

  const actions = [
    { key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: goEdit },
    { key: 'delete', label: L('fleetadmins_delete', 'Delete fleet admin'), icon: <DeleteOutlineRoundedIcon />, destructive: true, onClick: handleDelete },
  ];

  const addLabel = t('add_fleetadmin');
  const listTitle = calmCaps(t('fleetadmins_title'));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader primaryAction={{ label: addLabel, icon: <PersonAddAltRoundedIcon />, onClick: addFleetAdmin }} />
      <DataTable
        title={listTitle}
        rows={data}
        columns={columns}
        loading={!staticusers && !listFailed}
        error={listFailed ? { title: L('fleetadmins_load_failed', 'Could not load fleet admins.'), onRetry: () => dispatch(fetchUsersOnce()) } : null}
        countText={({ shown, total, query, loading }) =>
          loading
            ? L('loading', 'Loading…')
            : query
              ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total })
              : t('fleetadmins_count', {
                  count: total,
                  defaultValue_one: '{{count}} fleet admin',
                  defaultValue_other: '{{count}} fleet admins',
                  defaultValue: '{{count}} fleet admins',
                })
        }
        searchFn={searchFn}
        resortKey={allowPrivate ? 'private' : 'demo'}
        searchPlaceholder={allowPrivate ? L('drivers_search_hint', 'Search name, phone or email') : L('customers_search_name', 'Search by name')}
        rowLabel={nameOf}
        actions={actions}
        page={page}
        onPageChange={setPage}
        prefsKey="fleet_admins"
        stateKey="fleet_admins"
        exportCsv={allowPrivate}
        exportColumns={exportColumns}
        exportFileName="fleet_admins"
        exportTitle={listTitle}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <BusinessCenterOutlinedIcon />,
                title: L('fleetadmins_empty', 'No fleet admins yet'),
                body: L('fleetadmins_empty_body', 'Fleet admins manage their own drivers and cars.'),
                actionLabel: addLabel,
                onAction: addFleetAdmin,
              }
        }
        minWidth={760}
      />
    </Box>
  );
}
